import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getEffectiveApiUrl } from '../config/runtimeEnv';
import { useContextStore } from '../store/contextStore';
import { getSsoToken } from './ssoTokenProvider';

/**
 * Base URL for the API. Set via VITE_API_URL in .env or at build time; see runtimeEnv.ts.
 */
const API_BASE_URL = getEffectiveApiUrl();

/**
 * Axios instance configured for API requests.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth token and tenant/project context
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const authData = localStorage.getItem('agentops-auth-storage');
    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        const storedToken = parsed?.state?.token ?? parsed?.token ?? null;
        let token: string | null = await getSsoToken();
        if (!token) token = storedToken;

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else if (import.meta.env.DEV && authData) {
          console.warn('[api] No token available for request', config.url, { hasStored: !!storedToken });
        }
      } catch {
        // Ignore parse errors
      }
    }

    // Get tenant/project context from localStorage
    const contextData = localStorage.getItem('agentops-context-storage');
    if (contextData) {
      try {
        const parsed = JSON.parse(contextData);
        const currentTenant = parsed?.state?.currentTenant;
        const currentProject = parsed?.state?.currentProject;

        if (currentTenant?.id) {
          config.headers['X-Tenant-Id'] = currentTenant.id;
        }
        if (currentProject?.id) {
          config.headers['X-Project-Id'] = currentProject.id;
        }
      } catch {
        // Ignore parse errors
      }
    }

    // Default Content-Type: application/json breaks multipart uploads (415 on skills, etc.).
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else {
        delete (config.headers as Record<string, unknown>)['Content-Type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - clear auth and redirect to / (ProtectedRoute will trigger B2C login)
    if (error.response?.status === 401) {
      const skipAuthRedirect = (error.config as InternalAxiosRequestConfig & { skipAuthRedirect?: boolean })?.skipAuthRedirect;
      if (import.meta.env.DEV) {
        console.warn('[api] 401 Unauthorized', error.config?.url, error.config?.method, skipAuthRedirect ? '(skip redirect)' : '', error.response?.data);
      }
      if (!skipAuthRedirect) {
        localStorage.removeItem('agentops-auth-storage');
        useContextStore.getState().clearContext();
        window.location.href = '/';
      }
      return Promise.reject(error);
    }

    // Suppress console errors for 500 errors when backend is unavailable
    // These are expected during development when backend isn't running
    // Only log in development mode for debugging
    if (error.response?.status === 500 && import.meta.env.DEV) {
      // Silently handle 500 errors - they're expected when backend isn't available
      // The calling code will handle the error appropriately
      console.debug('Backend API error (expected if backend is not running):', error.config?.url);
    }

    return Promise.reject(error);
  }
);
